import { useEffect, useMemo, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import CopyButton from "@/components/affiliate/CopyButton";
import FilterChips from "@/components/affiliate/FilterChips";
import useProgramFromQuery from "@/lib/useProgramFromQuery";
import { affGet } from "@/lib/affiliateApi";
import { C } from "@/lib/brand";

// FAQ responses — Q&A format. Lets the affiliate copy a vetted answer
// when a follower DMs with a common question.
//
// API contract:
//   GET /affiliate/content/faqs →
//     { faqs: [{id, question, answer, programs:[]}] }
export default function AssetsFAQs() {
  const [faqs, setFaqs] = useState(null);
  const [program, setProgram] = useProgramFromQuery();
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await affGet("/affiliate/content/faqs", { auth: true });
        const items = res?.items || res?.faqs || [];
        const normalized = items.map((f) => ({
          id: f.id || f._id || f.contentId,
          // BE FAQ shape stores the question in `title` and the answer
          // in `body`. Older builds used question/answer directly.
          question: f.question || f.title || "",
          answer: f.answer || f.body || "",
          programs: f.programs || (f.programSlug ? [f.programSlug] : []),
        })).filter((f) => f.question && f.answer);
        if (!cancelled) setFaqs(normalized);
      } catch {
        if (!cancelled) setFaqs([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const programs = useMemo(() => {
    if (!faqs) return [];
    const set = new Set();
    faqs.forEach((f) => (f.programs || []).forEach((p) => set.add(p)));
    return Array.from(set);
  }, [faqs]);

  const visible = useMemo(() => {
    if (!faqs) return [];
    const q = query.trim().toLowerCase();
    return faqs.filter((f) => {
      if (program !== "all" && !(f.programs || []).includes(program)) return false;
      if (q && !(`${f.question} ${f.answer}`).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [faqs, program, query]);

  if (faqs === null) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "40px 24px 80px" }}>
      <Eyebrow color={C.peachDeep}>★ QUICK REFERENCE</Eyebrow>
      <Headline before="FAQ" italic="responses." size={48} className="mt-4" />
      <p style={{ marginTop: 14, fontSize: 15, color: C.warmGray, maxWidth: 640, lineHeight: 1.6 }}>
        Vetted answers to the questions your audience will actually ask. Copy as-is, or use them as a starting point.
      </p>
      <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 14 }}>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search questions…"
          data-testid="faqs-search"
          style={{ padding: "12px 14px", fontSize: 14, background: C.pure, border: `1px solid ${C.warmGray3}`, outline: "none" }}
        />
        {programs.length > 1 && (
          <FilterChips value={program} onChange={setProgram} options={programs} testIdPrefix="faqs-program" />
        )}
      </div>
      <div data-testid="faqs-list" style={{ marginTop: 28 }}>
        {visible.length === 0 ? (
          <div data-testid="faqs-empty" style={{ padding: 24, background: C.bone, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            No FAQs match these filters.
          </div>
        ) : (
          visible.map((f) => {
            const open = openId === f.id;
            return (
              <article
                key={f.id}
                data-testid={`faq-${f.id}`}
                style={{ borderTop: `1px solid ${C.warmGray3}`, padding: "18px 0" }}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : f.id)}
                  style={{
                    width: "100%",
                    background: "transparent",
                    border: 0,
                    padding: 0,
                    textAlign: "left",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 14,
                    color: C.ink,
                  }}
                >
                  <span style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.4 }}>
                    {f.question}
                  </span>
                  <span aria-hidden style={{ fontSize: 20, color: C.warmGray, transform: open ? "rotate(45deg)" : "rotate(0)", transition: "transform 160ms" }}>
                    +
                  </span>
                </button>
                {open && (
                  <div style={{ marginTop: 14 }}>
                    <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: C.ink, whiteSpace: "pre-wrap" }}>
                      {f.answer}
                    </p>
                    <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
                      <CopyButton text={f.answer} testId={`faq-copy-${f.id}`} label="COPY ANSWER" />
                    </div>
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}
